<template>
  <div>
    <span v-if="labelTop" class="lp-list-label font-weight-medium">
      {{ labelTop }}
      <span v-if="!preview && required" class="text-blue">*</span>
    </span>
    <span v-if="preview">
      {{
        options
          .filter((option) => isSelected(option))
          .map((option) => option[labelBy])
          .join(', ')
      }}
    </span>
    <div v-else class="lp-list-wrapper" data-ua-unique-id="unique-list">
      <lp-input
        v-if="search"
        v-model="searchValue.value"
        :placeholder="t('Shared.FilterOptions')"
        prefix-icon="search"
        size="compact"
      />
      <div class="lp-list mt-1">
        <lp-checkbox
          v-for="(option, index) in options"
          :id="option[trackBy] as string"
          :key="index"
          :model-value="isSelected(option)"
          :label="option[labelBy] as string"
          :disabled="option.disabled as boolean"
          @update:model-value="onToggle(option)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, reactive, ref, watch } from 'vue'
import { t } from '@/plugins/i18n'

export type ListOption = {
  [key: string]: unknown
}

defineOptions({
  name: 'lp-list',
})

interface Props {
  labelBy?: keyof ListOption
  trackBy?: keyof ListOption
  search?: boolean
  options: ListOption[]
  modelValue: ListOption[]
  preview?: boolean
  required?: boolean
  labelTop?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  labelBy: 'label',
  trackBy: 'id',
  search: false,
  options: () => [],
  modelValue: () => [],
  preview: false,
  required: false,
  labelTop: null,
})
const emit = defineEmits(['search-emit', 'list-updated'])

const selected = ref([...props.modelValue])
const searchValue = reactive({
  value: '',
})

watch(
  () => props.modelValue,
  () => {
    selected.value = [...props.modelValue]
  },
)
provide(
  'searchEmitValue',
  computed(() => searchValue.value?.toLowerCase()),
)

const listUpdated = () => {
  return selected.value.flatMap((item) =>
    props.options.filter(
      (option) => option[props.trackBy as keyof ListOption] === item,
    ),
  )
}

const onToggle = (option: ListOption) => {
  const position = selected.value.indexOf(option[props.trackBy] as ListOption)
  if (position !== -1) {
    selected.value.splice(position, 1)
  } else {
    selected.value.push(option[props.trackBy] as ListOption)
  }
  isSelected(option)
  emit('list-updated', selected.value, listUpdated())
}

const isSelected = (option: ListOption) => {
  return selected.value.indexOf(option[props.trackBy] as ListOption) !== -1
}
</script>
